// 자동 매칭 컴포넌트

import React, { useState, useEffect } from "react";
import { OriginalSong, Guide } from "../types/type";
import { linkGuideToOriginal } from "../api/originalSong.api";

// 자동 매칭을 위한 타입 정의
interface MatchingPair {
  guide: Guide;
  originalSong: OriginalSong;
  confidence: number; // 0~100 사이의 점수
  selected: boolean;
}

interface AutoMatchingSectionProps {
  guides: Guide[];
  originalSongs: OriginalSong[];
  onMatchingComplete: (
    pairs: { guideId: string; originalSongId: string }[]
  ) => void;
  setMessage: (message: string) => void;
}

const AutoMatchingSection: React.FC<AutoMatchingSectionProps> = ({
  guides,
  originalSongs,
  onMatchingComplete,
  setMessage,
}) => {
  const [matchingPairs, setMatchingPairs] = useState<MatchingPair[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [allSelected, setAllSelected] = useState<boolean>(true);

  // 가이드와 원곡 간의 매칭을 자동으로 찾는 함수
  useEffect(() => {
    findPotentialMatches();
  }, [guides, originalSongs]);

  const findPotentialMatches = () => {
    const pairs: MatchingPair[] = [];
    console.log("Guides:", guides.length);
    console.log("OriginalSongs:", originalSongs.length);

    const unmappedGuides = guides.filter((guide) => !guide.originalSongId);
    console.log("Unmapped guides:", unmappedGuides.length);

    unmappedGuides.forEach((guide) => {
      // 가이드 제목에서 검색 키워드 추출 (특수문자, 성별 관련 단어, 공백 등 제거)
      const cleanTitle = cleanGuideTitle(guide.guideTitle);

      // 제목이 비슷한 원곡 찾기
      originalSongs.forEach((originalSong) => {
        const similarity = calculateSimilarity(cleanTitle, originalSong.title);

        // 유사도가 특정 임계값(여기서는 70%) 이상인 경우만 추천
        if (similarity >= 70) {
          pairs.push({
            guide,
            originalSong,
            confidence: similarity,
            selected: true, // 기본적으로 선택된 상태
          });
        }
      });
    });

    // 신뢰도 점수 순으로 정렬
    pairs.sort((a, b) => b.confidence - a.confidence);

    setMatchingPairs(pairs);
  };

  // 가이드 제목에서 실제 곡 제목만 추출하는 함수
  const cleanGuideTitle = (title: string): string => {
    // "- 남자 가이드", "- 여자 가이드" 등의 패턴 제거
    let cleaned = title.replace(
      /\s*-\s*(남자|여자|Male|Female)\s*가이드/gi,
      ""
    );

    // 기타 정리 작업 (괄호 안 내용, 특수문자 등 제거)
    cleaned = cleaned.replace(/\([^)]*\)/g, ""); // 괄호와 그 안의 내용 제거
    cleaned = cleaned.replace(/[^\w\s가-힣]/g, ""); // 특수문자 제거
    cleaned = cleaned.trim(); // 앞뒤 공백 제거

    return cleaned;
  };

  // 두 문자열 간의 유사도를 계산하는 함수 (간단한 구현)
  const calculateSimilarity = (str1: string, str2: string): number => {
    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();

    // 완전 일치하는 경우
    if (s1 === s2) return 100;

    // 한 문자열이 다른 문자열을 포함하는 경우
    if (s1.includes(s2) || s2.includes(s1)) {
      // 포함된 문자열 길이 비율에 따라 점수 계산
      const shorter = s1.length < s2.length ? s1 : s2;
      const longer = s1.length < s2.length ? s2 : s1;
      return (shorter.length / longer.length) * 100;
    }

    // 편집 거리 기반 유사도 계산 (Levenshtein distance)
    const maxLen = Math.max(s1.length, s2.length);
    if (maxLen === 0) return 100;

    const editDistance = levenshteinDistance(s1, s2);
    return (1 - editDistance / maxLen) * 100;
  };

  // Levenshtein 거리 계산 함수
  const levenshteinDistance = (str1: string, str2: string): number => {
    const m = str1.length;
    const n = str2.length;

    // 빈 문자열인 경우 처리
    if (m === 0) return n;
    if (n === 0) return m;

    // 2차원 배열 초기화
    const dp: number[][] = [];
    for (let i = 0; i <= m; i++) {
      dp[i] = [];
      dp[i][0] = i;
    }

    for (let j = 0; j <= n; j++) {
      dp[0][j] = j;
    }

    // 편집 거리 계산
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1, // 삭제
          dp[i][j - 1] + 1, // 삽입
          dp[i - 1][j - 1] + cost // 대체
        );
      }
    }

    return dp[m][n];
  };

  // 모든 항목 선택/해제 토글
  const toggleSelectAll = () => {
    const newSelected = !allSelected;
    setAllSelected(newSelected);

    setMatchingPairs(
      matchingPairs.map((pair) => ({
        ...pair,
        selected: newSelected,
      }))
    );
  };

  // 개별 항목 선택/해제 토글
  const toggleSelectPair = (index: number) => {
    const updatedPairs = [...matchingPairs];
    updatedPairs[index].selected = !updatedPairs[index].selected;
    setMatchingPairs(updatedPairs);

    // 모든 항목이 선택되었는지 확인
    const allChecked = updatedPairs.every((pair) => pair.selected);
    setAllSelected(allChecked);
  };

  // 선택된 매칭 항목 일괄 연결
  const applySelectedMatches = async () => {
    try {
      setIsProcessing(true);

      const selectedPairs = matchingPairs.filter((pair) => pair.selected);

      if (selectedPairs.length === 0) {
        setMessage("선택된 항목이 없습니다.");
        setIsProcessing(false);
        return;
      }

      setMessage(`${selectedPairs.length}개 항목 연결 중...`);

      const completedMappings = [];

      // 각 매칭 쌍을 순차적으로 연결
      for (const pair of selectedPairs) {
        await linkGuideToOriginal(pair.guide.id, pair.originalSong.id);
        completedMappings.push({
          guideId: pair.guide.id,
          originalSongId: pair.originalSong.id,
        });
      }

      // 연결 완료 후 사용자에게 알림
      setMessage(`${selectedPairs.length}개 항목이 성공적으로 연결되었습니다.`);

      // 연결 완료 콜백 호출
      onMatchingComplete(completedMappings);

      // 매칭 목록에서 연결된 항목 제거
      setMatchingPairs(matchingPairs.filter((pair) => !pair.selected));
    } catch (error) {
      setMessage(`연결 중 오류가 발생했습니다: ${error}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // 매칭할 항목이 없는 경우
  if (matchingPairs.length === 0) {
    return (
      <div style={styles.noMatchesContainer}>
        <h3 style={styles.sectionTitle}>자동 매칭 추천</h3>
        <p style={styles.noMatchesText}>
          현재 자동으로 매칭할 수 있는 항목이 없습니다.
        </p>
        <button onClick={findPotentialMatches} style={styles.refreshButton}>
          매칭 재검색
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.sectionTitle}>
          자동 매칭 추천 ({matchingPairs.length}개)
        </h3>
        <div style={styles.actions}>
          <label style={styles.selectAllLabel}>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
              style={styles.checkbox}
            />
            전체 선택/해제
          </label>
          <button
            onClick={applySelectedMatches}
            disabled={
              isProcessing ||
              matchingPairs.filter((p) => p.selected).length === 0
            }
            style={{
              ...styles.applyButton,
              opacity:
                isProcessing ||
                matchingPairs.filter((p) => p.selected).length === 0
                  ? 0.5
                  : 1,
            }}
          >
            {isProcessing ? "처리 중..." : "선택한 매칭 일괄 적용"}
          </button>
        </div>
      </div>

      <div style={styles.matchListContainer}>
        <table style={styles.matchTable}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>선택</th>
              <th style={styles.tableHeader}>가이드 제목</th>
              <th style={styles.tableHeader}>원곡 제목</th>
              <th style={styles.tableHeader}>원곡 아티스트</th>
              <th style={styles.tableHeader}>매칭 신뢰도</th>
            </tr>
          </thead>
          <tbody>
            {matchingPairs.map((pair, index) => (
              <tr key={pair.guide.id} style={styles.tableRow}>
                <td style={styles.tableCell}>
                  <input
                    type="checkbox"
                    checked={pair.selected}
                    onChange={() => toggleSelectPair(index)}
                    style={styles.checkbox}
                  />
                </td>
                <td style={styles.tableCell}>
                  {pair.guide.guideTitle}
                  <span style={styles.genderTag}>
                    {pair.guide.isMale ? "남성" : "여성"}
                  </span>
                </td>
                <td style={styles.tableCell}>{pair.originalSong.title}</td>
                <td style={styles.tableCell}>{pair.originalSong.artistName}</td>
                <td style={styles.tableCell}>
                  <div style={styles.confidenceContainer}>
                    <div
                      style={{
                        ...styles.confidenceBar,
                        width: `${pair.confidence}%`,
                        backgroundColor: getConfidenceColor(pair.confidence),
                      }}
                    />
                    <span style={styles.confidenceText}>
                      {Math.round(pair.confidence)}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 신뢰도에 따른 색상 결정
const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 90) return "#4caf50"; // 높음 (녹색)
  if (confidence >= 75) return "#2196f3"; // 중간 (파란색)
  return "#ff9800"; // 낮음 (주황색)
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    padding: 16,
    marginBottom: 24,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#1976d2",
    margin: 0,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  selectAllLabel: {
    display: "flex",
    alignItems: "center",
    fontSize: 14,
    cursor: "pointer",
  },
  checkbox: {
    marginRight: 8,
    cursor: "pointer",
    width: "20px", // 체크박스 너비 증가
    height: "20px", // 체크박스 높이 증가
    accentColor: "#1976d2", // 체크 색상 변경 (선택 사항)
  },
  applyButton: {
    backgroundColor: "#4caf50",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: 4,
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: 14,
  },
  matchListContainer: {
    maxHeight: "400px",
    overflowY: "auto",
    backgroundColor: "white",
    borderRadius: 4,
    border: "1px solid #e0e0e0",
  },
  matchTable: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    textAlign: "left",
    padding: "12px 16px",
    borderBottom: "2px solid #e0e0e0",
    backgroundColor: "#f9f9f9",
    fontSize: 14,
    fontWeight: "bold",
    color: "#333",
  },
  tableRow: {
    borderBottom: "1px solid #e0e0e0",
  },
  tableCell: {
    padding: "12px 16px",
    fontSize: 14,
  },
  genderTag: {
    display: "inline-block",
    marginLeft: 8,
    padding: "2px 6px",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: "bold",
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
  },
  confidenceContainer: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  confidenceBar: {
    height: 8,
    borderRadius: 4,
    flex: 1,
  },
  confidenceText: {
    minWidth: "40px",
    fontSize: 14,
    fontWeight: "bold",
  },
  noMatchesContainer: {
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    padding: 24,
    marginBottom: 24,
    textAlign: "center",
  },
  noMatchesText: {
    margin: "16px 0",
    color: "#666",
  },
  refreshButton: {
    backgroundColor: "#1976d2",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 14,
  },
};

export default AutoMatchingSection;
